import clsx from 'clsx';
import { OrderInstruction } from 'components/maps/BundleMap';
import SelectCourierModal from 'components/modals/SelectCourierModal';
import MoreActionMenu from 'components/MoreActionMenu/MoreActionMenu';
import { Dayjs } from 'dayjs';
import { CourierVehicle } from 'pages/couriers/courierVehicle';
import { BundleMapWindow } from 'pages/oldPlanner/planner/BundleMapWindow';
import {
  getBundleColor,
  getBundleFontColor,
} from 'pages/oldPlanner/utils/bundleDraft';
import React, { MouseEvent, useState } from 'react';
import { FaMapMarkerAlt, FaUserCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { BundleJson, BundleStatus, InstructionType } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import { ChangePickupPointModal } from '../../components/ChangePickupPointModal';

interface BundleItemTopMenuProps {
  region: Region;
  activeDate: Dayjs;
  bundle: BundleJson;
  isDraft: boolean;
  checked: boolean;
  orderInstructions: Array<OrderInstruction>;
  activeBundleMap?: string;
  splitBundleActivated: boolean;
  moveToBundleActivated: boolean;
  onSelectDraftBundles: () => void;
  toggleExpand: () => void;
  reFetch: () => void;
  onInteractingWithBundleMap: (bundleId: string) => void;
  onActivateSplitBundle: () => void;
  onActivateMoveToBundle: () => void;
  onResetActivatedFeatures: () => void;
}

export const BundleItemTopMenu = ({
  bundle,
  onSelectDraftBundles,
  toggleExpand,
  reFetch,
  onInteractingWithBundleMap,
  onActivateSplitBundle,
  onActivateMoveToBundle,
  onResetActivatedFeatures,
  ...props
}: BundleItemTopMenuProps) => {
  const [courierModal, setCourierModal] = useState(false);
  const [bundleMap, setBundleMap] = useState(false);
  const [pickupPointModal, setPickupPointModal] = useState(false);

  const onOpenCourierModal = (event: MouseEvent<HTMLSpanElement>) => {
    /* Adding this Stop Event Propagation to prevent forwarding the Click Event to Parent component */
    /* Currently there is Click Event on the Parent component to Toggle Expand of the Orders inside Bundle */
    event.stopPropagation();

    setCourierModal(true);
  };

  const onCloseCourierModal = () => {
    setCourierModal(false);
  };

  const onSelectCourier = () => {
    reFetch();
    onCloseCourierModal();
  };

  const onOpenBundleMap = (event: MouseEvent<HTMLDivElement>) => {
    /* Adding this Stop Event Propagation to prevent forwarding the Click Event to Parent component */
    /* Currently there is Click Event on the Parent component to Toggle Expand of the Orders inside Bundle */
    event.stopPropagation();

    setBundleMap(true);
  };

  const onCloseBundleMap = () => {
    setBundleMap(false);
  };

  const onBundleMapFocus = () => {
    onInteractingWithBundleMap(bundle.id);
  };

  const onOpenPickupPointModal = (event: MouseEvent<HTMLDivElement>) => {
    /* Adding this Stop Event Propagation to prevent forwarding the Click Event to Parent component */
    /* Currently there is Click Event on the Parent component to Toggle Expand of the Orders inside Bundle */
    event.stopPropagation();

    setPickupPointModal(true);
  };

  const onClosePickupPointModal = (changed: boolean) => {
    if (changed) {
      reFetch();
    }
    setPickupPointModal(false);
  };

  const backgroundColor = getBundleColor(bundle.id);
  const color = getBundleFontColor(bundle.id);

  const isPublished = [
    BundleStatus.Published,
    BundleStatus.Proposed,
    BundleStatus.Accepted,
  ].includes(bundle.status);
  const ordersCount = bundle.instructions.length / 2;
  const completedOrdersCount = bundle.instructions.filter(
    instr => instr.type === InstructionType.Delivery && instr.completed
  ).length;

  const actionList = [
    {
      text: 'Assign courier',
      clickAction: onOpenCourierModal,
    },
    {
      text: 'Split bundle',
      clickAction: props.splitBundleActivated
        ? onResetActivatedFeatures
        : onActivateSplitBundle,
    },
    {
      text: 'Move to bundle',
      clickAction: props.moveToBundleActivated
        ? onResetActivatedFeatures
        : onActivateMoveToBundle,
    },
    {
      text: 'Set pickup point',
      clickAction: onOpenPickupPointModal,
    },
  ];

  return (
    <div
      style={{
        border: '1px solid #ccc',
      }}
      className="planner-bundle-header"
    >
      <div
        style={{ backgroundColor: backgroundColor }}
        className="planner-bundle-title-background"
      >
        <input
          id={`bundleTitle${bundle.id}`}
          type="checkbox"
          checked={props.checked}
          disabled={!props.isDraft}
          onChange={onSelectDraftBundles}
        />
        <Link
          to={`/bundles/${bundle.id}`}
          className="link__clean"
          target={'_blank'}
          style={{ color }}
        >
          <span>{bundle.id}</span>
        </Link>
      </div>
      <div className="planner-bundle-title">
        <div className="planner-bundle-title-line" onClick={toggleExpand}>
          <div className="planner-bundle-title-line-orders">
            <span>
              Orders:&nbsp;
              {isPublished
                ? `${completedOrdersCount} / ${ordersCount}`
                : ordersCount}
            </span>
          </div>

          {bundle.assignedCourierId && (
            <div className="planner-bundle-title-line-courier tooltip">
              <p className="tooltiptext">{bundle.assignedCourierName}</p>
              <div className="text-ellipsis-wrapper">
                <span onClick={onOpenCourierModal}>
                  <CourierVehicle
                    courier={{
                      travelMode: bundle.travelMode,
                      carType: bundle.carType,
                    }}
                  />{' '}
                  {bundle.assignedCourierName}
                </span>
              </div>
            </div>
          )}

          <div
            className={clsx(
              { 'no-courier': !bundle.assignedCourierId },
              'planner-bundle-title-line-icons'
            )}
          >
            {!bundle.assignedCourierId && (
              <div
                className="planner-bundle-title-line-courier"
                onClick={onOpenCourierModal}
              >
                <FaUserCheck />
              </div>
            )}
            <div
              className="planner-bundle-title-line-map"
              onClick={onOpenBundleMap}
            >
              <FaMapMarkerAlt />
            </div>
            <MoreActionMenu
              className="planner-bundle-title-line-more-menu"
              actionList={actionList}
              menuSide="left"
            />
          </div>
        </div>
      </div>

      {courierModal && (
        <SelectCourierModal
          bundleId={bundle.id}
          onCloseCourierModal={onCloseCourierModal}
          onSelectedCourier={onSelectCourier}
          assignedCourierId={
            bundle.assignedCourierId
              ? parseInt(bundle.assignedCourierId)
              : undefined
          }
          region={bundle.region}
          selectedDate={props.activeDate}
        />
      )}

      {props.orderInstructions.length > 0 && bundleMap && (
        <BundleMapWindow
          bundleId={bundle.id}
          orderInstructions={props.orderInstructions}
          onClose={onCloseBundleMap}
          onTop={props.activeBundleMap === bundle.id}
          onSetFocus={onBundleMapFocus}
          refreshZoomPosition={false}
          region={props.region}
        />
      )}

      {pickupPointModal && (
        <ChangePickupPointModal
          bundleId={bundle.id}
          bundleOrderIds={props.orderInstructions.map(
            instruction => instruction.order.id
          )}
          currentPickupAddress={
            props.orderInstructions?.[0].order?.originAddress
          }
          region={bundle.region}
          onCloseModal={onClosePickupPointModal}
        />
      )}
    </div>
  );
};
